import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface HebrewKeyboardProps {
    onCharClick: (char: string) => void;
    onClose: () => void;
}

export const HebrewKeyboard: React.FC<HebrewKeyboardProps> = ({ onCharClick, onClose }) => {
    // Standard Hebrew layout
    const rows = [
        ['ק', 'ר', 'א', 'ט', 'ו', 'ן', 'ם', 'פ'],
        ['ש', 'ד', 'ג', 'כ', 'ע', 'י', 'ח', 'ל', 'ך', 'ף'],
        ['ז', 'ס', 'ב', 'ה', 'נ', 'מ', 'צ', 'ת', 'ץ', '.']
    ];

    return (
        <div className="absolute top-full left-0 z-50 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 p-3 min-w-[280px] animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-2 px-1">
                <span className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">HEBREW KEYBOARD</span>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors">
                    <X size={14}/>
                </button>
            </div>
            <div className="flex flex-col gap-1.5">
                {rows.map((row, i) => (
                    <div key={i} className="flex justify-center gap-1.5">
                        {row.map(char => (
                            <button
                                key={char}
                                onMouseDown={(e) => {
                                    e.preventDefault(); // Prevent losing focus from input
                                    onCharClick(char);
                                }}
                                className="w-8 h-9 flex items-center justify-center bg-gray-50 border border-gray-100 hover:border-primary hover:bg-primary/5 hover:text-primary rounded-lg text-lg font-bold text-irongrey transition-all shadow-sm"
                            >
                                {char}
                            </button>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

interface HebrewDatePickerProps {
    value?: string;
    onSelect: (date: string) => void;
    onClose: () => void;
}

export const HebrewDatePicker: React.FC<HebrewDatePickerProps> = ({ value, onSelect, onClose }) => {
    const months = ['Tishrei', 'Cheshvan', 'Kislev', 'Tevet', 'Shevat', 'Adar', 'Adar II', 'Nissan', 'Iyar', 'Sivan', 'Tammuz', 'Av', 'Elul'];
    const days = Array.from({ length: 30 }, (_, i) => i + 1);
    const currentYear = new Date().getFullYear() + 3760; // Approximate Hebrew year
    const years = Array.from({ length: 20 }, (_, i) => currentYear - i);

    const [selectedDay, setSelectedDay] = useState<number | string>(days[0]);
    const [selectedMonth, setSelectedMonth] = useState<string>(months[0]);
    const [selectedYear, setSelectedYear] = useState<number | string>(years[0]);

    // Parse initial value if present (Simple parsing assuming "Day Month Year" format)
    React.useEffect(() => {
        if (value) {
            const parts = value.split(' ');
            if (parts.length >= 3) {
                if (!isNaN(parseInt(parts[0]))) setSelectedDay(parseInt(parts[0]));
                if (months.includes(parts[1])) setSelectedMonth(parts[1]);
                if (!isNaN(parseInt(parts[2]))) setSelectedYear(parseInt(parts[2]));
            }
        }
    }, []);

    const handleConfirm = () => {
        onSelect(`${selectedDay} ${selectedMonth} ${selectedYear}`);
        onClose();
    };

    return (
        <div className="absolute top-full left-0 z-50 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 p-4 min-w-[320px] animate-in fade-in zoom-in-95 duration-200">
             <div className="flex justify-between items-center mb-4 px-1">
                <span className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">HEBREW DATE</span>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors">
                    <X size={14}/>
                </button>
            </div>
            
            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="space-y-1">
                    <span className="text-[10px] font-bold text-irongrey/30 block text-center">DAY</span>
                    <div className="h-32 overflow-y-auto border border-gray-100 rounded-lg scrollbar-thin">
                        {days.map(d => (
                            <div 
                                key={d} 
                                onClick={() => setSelectedDay(d)}
                                className={`px-2 py-1.5 text-center text-sm font-medium cursor-pointer ${selectedDay === d ? 'bg-primary text-white' : 'hover:bg-gray-50 text-irongrey'}`}
                            >
                                {d}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="space-y-1">
                    <span className="text-[10px] font-bold text-irongrey/30 block text-center">MONTH</span>
                    <div className="h-32 overflow-y-auto border border-gray-100 rounded-lg scrollbar-thin">
                        {months.map(m => (
                            <div 
                                key={m} 
                                onClick={() => setSelectedMonth(m)}
                                className={`px-2 py-1.5 text-center text-sm font-medium cursor-pointer ${selectedMonth === m ? 'bg-primary text-white' : 'hover:bg-gray-50 text-irongrey'}`}
                            >
                                {m}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="space-y-1">
                    <span className="text-[10px] font-bold text-irongrey/30 block text-center">YEAR</span>
                    <div className="h-32 overflow-y-auto border border-gray-100 rounded-lg scrollbar-thin">
                        {years.map(y => (
                            <div 
                                key={y} 
                                onClick={() => setSelectedYear(y)}
                                className={`px-2 py-1.5 text-center text-sm font-medium cursor-pointer ${selectedYear === y ? 'bg-primary text-white' : 'hover:bg-gray-50 text-irongrey'}`}
                            >
                                {y}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <button 
                onClick={handleConfirm}
                className="w-full bg-primary text-white font-bold text-sm py-2 rounded-lg hover:bg-primary-hover transition-colors"
            >
                Confirm Date
            </button>
        </div>
    );
};